import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shareService } from '../services/shareService';
import { parseShareError } from '../utils/shareError';

export interface ShareBreadcrumbItem {
  id: string;
  name: string;
}

/**
 * Encapsula as três queries usadas pela SharePage (root, subfolder, file
 * stream) + estados de senha, breadcrumb e arquivo em visualização.
 */
export function useShareNavigation(token: string | undefined) {
  const [passwordInput, setPasswordInput] = useState('');
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [breadcrumb, setBreadcrumb] = useState<ShareBreadcrumbItem[]>([]);
  const [viewingFileId, setViewingFileId] = useState<string | null>(null);

  const rootQuery = useQuery({
    queryKey: ['share-access', token, password],
    queryFn: () => shareService.accessShare(token!, password),
    enabled: !!token,
    retry: false,
  });

  const currentFolderId = breadcrumb.length
    ? breadcrumb[breadcrumb.length - 1].id
    : undefined;

  const subfolderQuery = useQuery({
    queryKey: ['share-folder', token, password, currentFolderId],
    queryFn: () =>
      shareService.accessFolderInShare(token!, currentFolderId!, password),
    enabled: !!token && !!currentFolderId && !rootQuery.isError,
    retry: false,
  });

  const fileViewQuery = useQuery({
    queryKey: ['share-file-stream', token, password, viewingFileId],
    queryFn: () =>
      shareService.streamFileInShare(token!, viewingFileId!, password),
    enabled: !!token && !!viewingFileId,
    retry: false,
  });

  const activeError =
    rootQuery.error ?? subfolderQuery.error ?? fileViewQuery.error;
  const parsedError = useMemo(
    () => (activeError ? parseShareError(activeError) : null),
    [activeError],
  );

  return {
    rootQuery,
    subfolderQuery,
    fileViewQuery,
    parsedError,
    password,
    passwordInput,
    setPasswordInput,
    submitPassword: () => passwordInput && setPassword(passwordInput),
    breadcrumb,
    pushBreadcrumb: (id: string, name: string) =>
      setBreadcrumb((prev) => [...prev, { id, name }]),
    truncateBreadcrumbTo: (index: number) =>
      setBreadcrumb((prev) => prev.slice(0, index)),
    viewingFileId,
    setViewingFileId,
  };
}
